import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { queryKeys } from "./hooks";
import { isServerAnsweredFailure } from "./queryClient";

/**
 * Manual sessions: the clinician plays the patient, turn by turn, from the Lab.
 *
 * FOUR REQUESTS on the Project's preview family:
 *
 *  - `POST …/preview-manual-sessions`                          — create (201, no link);
 *  - `GET  …/preview-manual-sessions/{previewRunRef}`          — body-free state;
 *  - `POST …/preview-manual-sessions/{previewRunRef}/turns`    — one patient line;
 *  - the existing replay command with `continueManually: true`.
 *
 * The turn body carries patient text. It lives in the mutation variables and
 * the component that typed it — never in a query key, a URL, storage or a log
 * line. Turns spend model calls, so the turn mutation does not retry on its
 * own (`retry: false`); a DELIBERATE retry under the same idempotency
 * key and `expectedTurn` is safe because the service answers an already
 * delivered turn with `replayed: true` instead of submitting it again.
 */
export type PreviewManualSessionState =
  components["schemas"]["PreviewManualState"];
export type CreatePreviewManualSessionRequest =
  components["schemas"]["CreatePreviewManualSessionRequest"];
export type PreviewManualSessionCreated =
  components["schemas"]["PreviewManualSessionCreated"];
export type SendPreviewManualTurnRequest =
  components["schemas"]["SendPreviewManualTurnRequest"];
export type PreviewManualTurnResult =
  components["schemas"]["PreviewManualTurnResult"];

export const manualQueryKeys = {
  session: (projectId: string, previewRunRef: string) =>
    ["projects", projectId, "preview-manual-sessions", previewRunRef] as const,
};

/** How often a busy session's state is re-read. */
export const MANUAL_POLL_INTERVAL_MS = 2_000;

/** Extra run-envelope reads after a send; two, so upstream Liv's ~10 reads per 10 s hold. */
const TURN_REREAD_DELAYS_MS = [1_000, 2_500] as const;

export function isManualBusy(state: PreviewManualSessionState): boolean {
  return state.status === "busy";
}

/** One read of a session's state; a 404 (not manual / unknown) is `null`, as for sim runs. */
export async function readManualState(
  client: ReturnType<typeof useApiClient>,
  projectId: string,
  previewRunRef: string,
): Promise<PreviewManualSessionState | null> {
  try {
    return unwrap(
      await client.GET(
        "/v1/projects/{projectId}/preview-manual-sessions/{previewRunRef}",
        { params: { path: { projectId, previewRunRef } } },
      ),
    );
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export function manualRefetchInterval(
  state: { status: string; data: PreviewManualSessionState | null | undefined },
  pollIntervalMs: number | false = MANUAL_POLL_INTERVAL_MS,
): number | false {
  const data = state.data;
  return pollIntervalMs === false ||
    state.status !== "success" ||
    data === null ||
    data === undefined ||
    !isManualBusy(data)
    ? false
    : pollIntervalMs;
}

/**
 * The manual state of one run. `null` = not a manual session (the generic 404),
 * which the Lab asks of every run. Polls while the session is busy.
 */
export function usePreviewManualSessionQuery(
  projectId: string,
  previewRunRef: string,
  {
    enabled = true,
    pollIntervalMs = MANUAL_POLL_INTERVAL_MS,
  }: { enabled?: boolean; pollIntervalMs?: number | false } = {},
): UseQueryResult<PreviewManualSessionState | null> {
  const client = useApiClient();
  return useQuery({
    queryKey: manualQueryKeys.session(projectId, previewRunRef),
    queryFn: () => readManualState(client, projectId, previewRunRef),
    enabled: enabled && projectId.length > 0 && previewRunRef.length > 0,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchInterval: (query) =>
      manualRefetchInterval(query.state, pollIntervalMs),
  });
}

export function useCreatePreviewManualSessionMutation(
  projectId: string,
): UseMutationResult<
  PreviewManualSessionCreated,
  unknown,
  CreatePreviewManualSessionRequest
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(body: CreatePreviewManualSessionRequest) {
      return unwrap(
        await client.POST("/v1/projects/{projectId}/preview-manual-sessions", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    // The run may become the qualifying preview the Lab's Accept button reads.
    onSuccess() {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectOverview(projectId),
      });
    },
    retry: false,
  });
}

/**
 * Submit one patient line. Whatever the outcome, the session's state, the run
 * envelope and its events are re-read (the turn may have landed even when the
 * answer was lost). Only the envelope and the events list are invalidated, not
 * the per-turn detail reads: earlier turns' text does not change.
 */
export function useSendManualTurnMutation(
  projectId: string,
  previewRunRef: string,
): UseMutationResult<
  PreviewManualTurnResult,
  unknown,
  SendPreviewManualTurnRequest
> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    async mutationFn(body: SendPreviewManualTurnRequest) {
      return unwrap(
        await client.POST(
          "/v1/projects/{projectId}/preview-manual-sessions/{previewRunRef}/turns",
          { params: { path: { projectId, previewRunRef } }, body },
        ),
      );
    },
    retry: false,
    onSettled() {
      void queryClient.invalidateQueries({
        queryKey: manualQueryKeys.session(projectId, previewRunRef),
      });
      const rereadRun = () =>
        void queryClient.invalidateQueries({
          queryKey: queryKeys.previewRun(projectId, previewRunRef),
          exact: true,
        });
      rereadRun();
      // Liv's turn lands in the trace just after her answer; without these the
      // next regular poll shows it up to RUN_POLL_INTERVAL_MS later.
      for (const delay of TURN_REREAD_DELAYS_MS) {
        window.setTimeout(rereadRun, delay);
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.previewRunEvents(projectId, previewRunRef),
        exact: true,
      });
    },
  });
}


/** Why a turn was not answered with a 200, by what came back. Kinds only. */
export type ManualTurnFailure =
  /** 409 `manual_turn_busy` — a turn or a replay is in the way. */
  | { kind: "busy" }
  /** 409 `manual_turn_stale` — the session moved; re-read it and send again. */
  | { kind: "stale"; currentTurn: number | undefined }
  /** 409 `manual_turn_unanswered` — Liv's reply to the last line failed; `currentTurn` is the last turn she answered. */
  | { kind: "unanswered"; currentTurn: number | undefined }
  /** 409 `manual_session_ended` — the conversation is over. */
  | { kind: "ended" }
  /** 404 — not a manual session of this project (any more). */
  | { kind: "not_found" }
  | { kind: "denied" }
  /** 502/503/504 or no answer: WHETHER THE TURN LANDED IS UNKNOWN. Retry with the same key. */
  | { kind: "unknown" }
  | { kind: "error"; code: string };

export function classifyManualTurnFailure(error: unknown): ManualTurnFailure {
  if (!isServerAnsweredFailure(error) || !(error instanceof ApiError)) {
    return { kind: "unknown" };
  }
  if (error.code === "manual_turn_busy") {
    return { kind: "busy" };
  }
  if (error.code === "manual_turn_stale") {
    const current = error.envelope["currentTurn"];
    return {
      kind: "stale",
      currentTurn: typeof current === "number" ? current : undefined,
    };
  }
  if (error.code === "manual_turn_unanswered") {
    const current = error.envelope["currentTurn"];
    return {
      kind: "unanswered",
      currentTurn: typeof current === "number" ? current : undefined,
    };
  }
  if (error.code === "manual_session_ended") {
    return { kind: "ended" };
  }
  if (error.status === 404) {
    return { kind: "not_found" };
  }
  if (error.isForbidden || error.isUnauthenticated) {
    return { kind: "denied" };
  }
  if (error.status === 502 || error.status === 503 || error.status === 504) {
    return { kind: "unknown" };
  }
  return { kind: "error", code: error.code };
}

